import { useState } from 'react'
import { Link } from 'react-router-dom'

// Single nav row. One accent for state (active pill + left tick), color reserved
// for semantics (red badge). `sidebar-nav-link` class is required: global link
// hover styling in interactions.css excludes it.
const SidebarItem = ({
    path,
    icon: Icon,
    label,
    isActive,
    badge,          // null | { count: number } — count > 0 renders a count pill, else a dot
    isCollapsed,
    onClick,
}) => {
    // Fixed-position rail tooltip: overflow-y-auto on the nav container clips
    // absolute-positioned tooltips, so rail hints anchor to viewport coords.
    const [tip, setTip] = useState(null)

    // Tutorial spotlight hook (additive; PRD FR-7 / art spec §2): /scavenging
    // -> nav-scavenging, /gathering -> nav-gatherings, /crafting -> nav-crafting,
    // /vendors -> nav-vendors. No styling/behavior change.
    const tutorialHook = path.startsWith('/') ? `nav-${path.slice(1)}` : null

    const showTip = (e) => {
        const rect = e.currentTarget.getBoundingClientRect()
        setTip({ top: rect.top + rect.height / 2, left: rect.right + 8 })
    }
    const hideTip = () => setTip(null)
    const showCount = badge && badge.count > 0
    const badgeEl = badge && (
        showCount ? (
            <span className="ml-auto flex-shrink-0 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-red text-[10px] font-bold text-white">
                {badge.count}
            </span>
        ) : (
            <span className="ml-auto flex-shrink-0 h-2 w-2 rounded-full bg-accent-red" />
        )
    )

    if (isCollapsed) {
        return (
            <div className="group relative flex justify-center">
                <Link
                    to={path}
                    onClick={onClick}
                    onMouseEnter={showTip}
                    onMouseLeave={hideTip}
                    aria-label={label}
                    aria-current={isActive ? 'page' : undefined}
                    data-tutorial={tutorialHook}
                    className={`sidebar-nav-link relative flex h-11 w-11 items-center justify-center rounded-lg transition-colors duration-150 ${
                        isActive
                            ? 'bg-bg-hover text-text-lighter'
                            : 'text-text-muted hover:bg-bg-hover/50 hover:text-text-light'
                    }`}
                >
                    {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-accent-blue" />
                    )}
                    <Icon size={20} className={isActive ? 'text-accent-blue' : ''} />
                    {badge && (
                        <span
                            className={`absolute top-1.5 right-1.5 rounded-full bg-accent-red ${
                                showCount
                                    ? 'min-w-[14px] h-[14px] px-0.5 flex items-center justify-center text-[9px] font-bold text-white'
                                    : 'h-2 w-2 ring-2 ring-bg-darkest'
                            }`}
                        >
                            {showCount && badge.count > 9 ? '9+' : showCount ? badge.count : ''}
                        </span>
                    )}
                </Link>
                {tip && (
                    <div
                        className="pointer-events-none fixed z-[70] -translate-y-1/2 whitespace-nowrap rounded-md border border-border-light bg-bg-elevated px-2 py-1 text-xs text-text-light shadow-md"
                        style={{ top: tip.top, left: tip.left }}
                    >
                        {label}
                        <div className="absolute top-1/2 -left-1 -mt-1 border-4 border-transparent border-r-border-light" />
                    </div>
                )}
            </div>
        )
    }

    return (
        <Link
            to={path}
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
            data-tutorial={tutorialHook}
            className={`sidebar-nav-link relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                    ? 'bg-bg-hover text-text-lighter'
                    : 'text-text-muted hover:bg-bg-hover/50 hover:text-text-light'
            }`}
        >
            {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-full bg-accent-blue" />
            )}
            <Icon size={18} className={`flex-shrink-0 ${isActive ? 'text-accent-blue' : ''}`} />
            <span className="truncate">{label}</span>
            {badgeEl}
        </Link>
    )
}

export default SidebarItem
